import { useEffect, type ComponentType } from 'react';
import { useCommandPaletteContext } from '../CommandPaletteContext';

/**
 * Options for the useRegisterPage hook
 */
export interface UseRegisterPageOptions {
  /**
   * Unique identifier for the owning plugin
   */
  pluginId: string;

  /**
   * Unique identifier for this page within the plugin
   */
  pageId: string;

  /**
   * The React component to render when the palette is navigated to this page.
   * It replaces the default command list and has full control over what is
   * rendered inside the palette's `Command` (cmdk) element.
   *
   * Use the exported `CommandList` component inside your page if you want the
   * standard scored/filtered list behaviour, or render completely custom content.
   */
  component: ComponentType;

  /**
   * Whether the page is enabled. If false, the page component will not be registered.
   * @default true
   */
  enabled?: boolean;
}

/**
 * Hook for registering a plugin page into the command palette.
 *
 * A page is a scoped view within the command palette. When the user navigates
 * to a page (via `navigateTo`), the palette renders the registered component
 * instead of the default global command list.
 *
 * Use `CommandList` inside your page component for standard filtered list
 * behaviour, or render any arbitrary content.
 *
 * @example
 * ```tsx
 * const WorkspacePage: React.FC = () => {
 *   const commands = useMemo(() => [...], []);
 *   return <CommandList commands={commands} />;
 * };
 *
 * export const MyPlugin: React.FC = () => {
 *   const { navigateTo } = usePluginNavigation('my-plugin');
 *
 *   useRegisterCommands({
 *     pluginId: 'my-plugin',
 *     commands: [{
 *       id: 'open-my-page',
 *       label: 'Browse Items',
 *       category: 'My Plugin',
 *       primary: { id: 'action', label: 'Browse', action: () => navigateTo('items') },
 *     }],
 *   });
 *
 *   useRegisterPage({ pluginId: 'my-plugin', pageId: 'items', component: WorkspacePage });
 *
 *   return null;
 * };
 * ```
 */
export function useRegisterPage({
  pluginId,
  pageId,
  component,
  enabled = true,
}: UseRegisterPageOptions): void {
  const { registerPage, unregisterPage } = useCommandPaletteContext();

  useEffect(() => {
    if (enabled) {
      registerPage(pluginId, pageId, component);
    }

    return () => {
      unregisterPage(pluginId, pageId);
    };
  }, [pluginId, pageId, component, enabled, registerPage, unregisterPage]);
}
